/**
 * TrainAIDialog - Add training examples to teach the AI classification rules
 * 
 * Single Responsibility: Manage AI training example creation
 * Interface: Receives current training state and submission handler
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Brain, ArrowUpRight, ArrowDownRight, Loader2 } from 'lucide-react';
import { GLAccountSearchableSelect } from '../common/GLAccountSelector';

const INITIAL_TRAINING_EXAMPLE = {
  description: '',
  counterparty: '',
  direction: 'outflow',
  classification: '',
  gl_code: '',
  gl_name: '',
  amount_range: 'any',
  notes: ''
};

const OUTFLOW_CLASSIFICATIONS = [
  { value: 'bill_payment', label: 'Bill Payment' },
  { value: 'other_payment', label: 'Other Payment' },
  { value: 'loan_repayment', label: 'Loan Repayment' },
  { value: 'owner_withdrawal', label: 'Owner Withdrawal' },
  { value: 'bank_charges', label: 'Bank Charges' },
  { value: 'tax_payment', label: 'Tax Payment' },
  { value: 'salary_payment', label: 'Salary Payment' }
];

const INFLOW_CLASSIFICATIONS = [
  { value: 'invoice_payment', label: 'Invoice Payment' },
  { value: 'other_receipt', label: 'Other Receipt' },
  { value: 'loan_received', label: 'Loan Received' },
  { value: 'owner_contribution', label: 'Owner Contribution' },
  { value: 'interest_received', label: 'Interest Received' },
  { value: 'refund_received', label: 'Refund Received' }
];

export const TrainAIDialog = ({
  open,
  onOpenChange,
  onSubmit,
  submitting = false,
  glAccounts = [],
  prefillData = null
}) => {
  const [trainingExample, setTrainingExample] = useState(INITIAL_TRAINING_EXAMPLE);

  // Reset or prefill form when dialog opens
  useEffect(() => {
    if (open) {
      if (prefillData) {
        setTrainingExample({
          ...INITIAL_TRAINING_EXAMPLE,
          description: prefillData.description || '',
          counterparty: prefillData.counterparty || '',
          direction: prefillData.direction || 'outflow'
        });
      } else {
        setTrainingExample(INITIAL_TRAINING_EXAMPLE);
      }
    }
  }, [open, prefillData]);

  const handleClose = () => {
    setTrainingExample(INITIAL_TRAINING_EXAMPLE);
    onOpenChange(false);
  };

  const handleSubmit = () => {
    if (trainingExample.classification && (trainingExample.counterparty || trainingExample.description)) {
      onSubmit(trainingExample);
    }
  };

  const updateField = (field, value) => {
    setTrainingExample(prev => ({ ...prev, [field]: value }));
  };

  const classificationOptions = trainingExample.direction === 'outflow' 
    ? OUTFLOW_CLASSIFICATIONS 
    : INFLOW_CLASSIFICATIONS;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-600" />
            Train AI - Add Classification Rule
          </DialogTitle>
          <DialogDescription>
            Teach the AI how to classify transactions like this one
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Direction & Amount Range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Transaction Direction</Label>
              <Select 
                value={trainingExample.direction} 
                onValueChange={(v) => {
                  updateField('direction', v);
                  updateField('classification', ''); // Reset classification when direction changes
                }}
              >
                <SelectTrigger data-testid="training-direction">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="outflow">
                    <span className="flex items-center gap-2">
                      <ArrowUpRight className="w-4 h-4 text-red-600" />
                      Money Out (Payment)
                    </span>
                  </SelectItem>
                  <SelectItem value="inflow">
                    <span className="flex items-center gap-2">
                      <ArrowDownRight className="w-4 h-4 text-green-600" />
                      Money In (Receipt)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Amount Range (Optional)</Label>
              <Select 
                value={trainingExample.amount_range} 
                onValueChange={(v) => updateField('amount_range', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Any amount" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any amount</SelectItem>
                  <SelectItem value="small">Small (&lt; 1,000)</SelectItem>
                  <SelectItem value="medium">Medium (1,000 - 10,000)</SelectItem>
                  <SelectItem value="large">Large (&gt; 10,000)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Counterparty */}
          <div className="space-y-2">
            <Label>Vendor/Customer Name (Most Important)</Label>
            <Input
              value={trainingExample.counterparty}
              onChange={(e) => updateField('counterparty', e.target.value)}
              placeholder="e.g., Amazon, Shell, MTN"
              data-testid="training-counterparty"
            />
            <p className="text-xs text-muted-foreground">
              AI will match transactions from this vendor/customer
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Example Description</Label>
            <Input
              value={trainingExample.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="e.g., AWS monthly subscription"
              data-testid="training-description"
            />
            <p className="text-xs text-muted-foreground">
              Include keywords that typically appear in similar transactions
            </p>
          </div>

          {/* Classification */}
          <div className="space-y-2">
            <Label>Classification *</Label>
            <Select 
              value={trainingExample.classification} 
              onValueChange={(v) => updateField('classification', v)}
            >
              <SelectTrigger data-testid="training-classification">
                <SelectValue placeholder="Select classification" />
              </SelectTrigger>
              <SelectContent>
                {classificationOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* GL Account (Optional) */}
          <div className="space-y-2">
            <Label>Default GL Account (Optional)</Label>
            <GLAccountSearchableSelect
              value={trainingExample.gl_code}
              onChange={(v) => {
                const account = glAccounts.find(a => a.code === v);
                updateField('gl_code', v);
                updateField('gl_name', account?.name || '');
              }}
              placeholder="Select GL account"
              triggerTestId="training-gl"
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes (Optional)</Label>
            <Textarea
              value={trainingExample.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              placeholder="Any additional context for this rule"
              rows={2}
            />
          </div>

          {/* Info Box */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
            <p className="text-xs text-purple-700">
              <strong>How it works:</strong> The AI will use this example to classify similar transactions automatically. 
              The more examples you provide, the better the AI becomes at classifying your transactions.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!trainingExample.classification || (!trainingExample.counterparty && !trainingExample.description) || submitting}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="submit-training-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Add Training Rule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TrainAIDialog;
